import { getSmoothStepPath, Position, type XYPosition } from '@xyflow/react';

import { getUnroundedSmoothStepPoints } from '@/components/diagrams/FlowChart/edges/crossingGaps';
import { stripRedundantPoints } from '@/components/diagrams/FlowChart/edges/orthogonalPath';
import {
  addElbowCorners,
  isVerticalSide,
} from '@/components/diagrams/FlowChart/edges/requestedEdgePath';

import { BUILDER_EDGE_ENDPOINT_STUB } from './reactFlowDiagram';

/*
 * The geometry of a builder line that the user routes by hand. A line goes
 * from its source handle to its target handle through its bend points:
 *
 * - An elbow line stays horizontal and vertical. The user drags an inner
 *   segment sideways. The first and last segments hold the line on its
 *   nodes, so they do not move.
 * - A straight or curved line goes through its bend points. The user drags
 *   the middle of a segment to add a bend point there, or drags a bend point.
 */

/** A handle on a segment shorter than this, in canvas pixels, only adds clutter. */
const MIN_HANDLE_SEGMENT = 28;
/** A handle stays this far from the ends of its segment, in canvas pixels. */
const HANDLE_END_MARGIN = MIN_HANDLE_SEGMENT / 2;
/**
 * The distance from the edge of a label to the center of a handle that
 * clears the label, in canvas pixels: the 5px handle radius and a 7px gap.
 */
const HANDLE_LABEL_CLEARANCE = 12;
/** The middle of a segment, as a fraction of its length. */
const SEGMENT_MIDDLE = 0.5;

/** The box of a line label, in canvas pixels. */
export interface LineLabelBox {
  center: XYPosition;
  width: number;
  height: number;
}

/** The ends of a line, at the handles that it connects. */
export interface LineEnds {
  source: XYPosition;
  sourceSide: Position;
  target: XYPosition;
  targetSide: Position;
}

/** A drag handle on a line. `index` is the first point of its segment. */
export interface LineHandle {
  point: XYPosition;
  index: number;
  /** For an elbow segment: the axis that the segment moves along. */
  axis?: 'x' | 'y';
}

const midpoint = (a: XYPosition, b: XYPosition) => ({
  x: (a.x + b.x) / 2,
  y: (a.y + b.y) / 2,
});
const length = (a: XYPosition, b: XYPosition) =>
  Math.hypot(b.x - a.x, b.y - a.y);

/**
 * Returns every point of an elbow line from the source to the target, as the
 * shared arrow draws it. With `bends`, the line goes through them and turns
 * once between two points that do not share an axis. Without bends, it
 * follows the React Flow smooth step route.
 */
export function getElbowRoute(
  { source, sourceSide, target, targetSide }: LineEnds,
  bends: XYPosition[]
): XYPosition[] {
  if (bends.length > 0) {
    return stripRedundantPoints(
      addElbowCorners(
        [source, ...bends, target],
        isVerticalSide(sourceSide),
        isVerticalSide(targetSide)
      )
    );
  }
  const [path] = getSmoothStepPath({
    sourceX: source.x,
    sourceY: source.y,
    sourcePosition: sourceSide,
    targetX: target.x,
    targetY: target.y,
    targetPosition: targetSide,
    offset: BUILDER_EDGE_ENDPOINT_STUB,
    borderRadius: 0,
  });
  return stripRedundantPoints(getUnroundedSmoothStepPoints(path));
}

/**
 * Returns the drag handles of a line through `route`. An elbow line has a
 * handle on each inner segment. A straight or curved line has a handle in
 * the middle of each segment.
 *
 * A handle does not cover `label`. When the label box, made larger by the
 * clearance, contains the middle of a segment, the handle moves along the
 * segment to the nearer edge of that box. A segment without space for the
 * handle outside the box has no handle.
 */
export function getLineHandles(
  route: XYPosition[],
  isElbow: boolean,
  label?: LineLabelBox
): LineHandle[] {
  const first = isElbow ? 1 : 0;
  const last = isElbow ? route.length - 3 : route.length - 2;
  const handles: LineHandle[] = [];
  for (let index = first; index <= last; index++) {
    const from = route[index]!;
    const to = route[index + 1]!;
    if (length(from, to) < MIN_HANDLE_SEGMENT) continue;
    const point = label
      ? getPointClearOfLabel(from, to, label)
      : midpoint(from, to);
    if (!point) continue;
    handles.push({
      point,
      index,
      axis: isElbow ? (Math.abs(from.y - to.y) < 0.5 ? 'y' : 'x') : undefined,
    });
  }
  return handles;
}

/**
 * Returns the middle of the segment from `from` to `to` when the label does
 * not cover it. Otherwise, returns the nearest point of the segment at the
 * edge of the label box plus the clearance, or null when that point is too
 * near an end of the segment.
 */
function getPointClearOfLabel(
  from: XYPosition,
  to: XYPosition,
  label: LineLabelBox
): XYPosition | null {
  // The part of the segment in the box, as fractions of the segment, from
  // where it goes in to where it goes out.
  let enter = -Infinity;
  let exit = Infinity;
  for (const axis of ['x', 'y'] as const) {
    const half =
      (axis === 'x' ? label.width : label.height) / 2 + HANDLE_LABEL_CLEARANCE;
    const step = to[axis] - from[axis];
    const low = label.center[axis] - half - from[axis];
    const high = label.center[axis] + half - from[axis];
    if (step === 0) {
      if (low >= 0 || high <= 0) return midpoint(from, to);
      continue;
    }
    enter = Math.max(enter, Math.min(low / step, high / step));
    exit = Math.min(exit, Math.max(low / step, high / step));
  }
  if (SEGMENT_MIDDLE <= enter || SEGMENT_MIDDLE >= exit) {
    return midpoint(from, to);
  }

  // When the nearer edge is too near an end of the segment, the other edge
  // is nearer to an end too.
  const at = SEGMENT_MIDDLE - enter <= exit - SEGMENT_MIDDLE ? enter : exit;
  const margin = HANDLE_END_MARGIN / length(from, to);
  if (at < margin || at > 1 - margin) return null;
  return {
    x: from.x + (to.x - from.x) * at,
    y: from.y + (to.y - from.y) * at,
  };
}

/**
 * Returns the bend points after the user drags `handle` to `point`. An elbow
 * segment moves along its axis. A straight or curved segment gets a bend
 * point at `point`. The result has every inner point of the line, so the
 * saved line matches the line on the screen.
 */
export function dragLineHandle(
  route: XYPosition[],
  handle: LineHandle,
  point: XYPosition
): XYPosition[] {
  const moved = route.map((each) => ({ ...each }));
  if (handle.axis) {
    moved[handle.index]![handle.axis] = point[handle.axis];
    moved[handle.index + 1]![handle.axis] = point[handle.axis];
  } else {
    moved.splice(handle.index + 1, 0, point);
  }
  return moved.slice(1, -1).map(({ x, y }) => ({
    x: Math.round(x),
    y: Math.round(y),
  }));
}
